/**
 * The conversational interview (`POST /api/onboarding/interview`, SSE —
 * PIVOT-041/083). Opens with the server's first question on mount; each reply
 * gets back one `event: turn` block — a full JSON-serialized `InterviewTurn`
 * (`message`, `done`, `choices`, ...), not token-by-token text — until a turn's
 * own `done` field is true. There is no separate `done` SSE event.
 *
 * The conversation lives server-side, on the candidate's onboarding draft row,
 * so this component holds only what it renders. Opening with `null` is
 * idempotent: on a reload the server replays the turn the candidate was last
 * shown rather than restarting the interview.
 *
 * `choices` (up to 3 short options) renders as a lettered option list *below*
 * the question, outside its box: the question is one card, each choice is its
 * own card. The free-text box is the last lettered option ("D. Chat about
 * this"), so writing an answer is presented as one of the options rather than
 * as a separate control the backend never offered.
 *
 * `choices_multi` on the turn switches that list from one-tap-submits to
 * multi-select: tapping toggles a pick, and a confirm button sends every
 * picked label as one user message, joined with "; ". The backend parses
 * nothing new for this — a choice has always been canned free text submitted
 * as the answer — so the joined string arrives as an ordinary reply. Some
 * questions are unanswerable without it: asked which part of four years of
 * work she wanted at the centre of what came next, a candidate's honest answer
 * was all three options, and one tap can only say one of them. The values
 * question has the same shape. Without multi-select the only truthful route
 * was to ignore the options and type, which is the option a candidate is
 * least likely to take.
 *
 * A "directions" turn (the lateral-move pitch, PIVOT-022) carries its options
 * in `suggested_directions` instead of `choices` — a different field on the
 * same `InterviewTurn`, because the backend also uses `suggested_directions`
 * outside the wire format (the profile builder reads it back). Rendering
 * fell back to `choices` only, so the three pitched directions used to reach
 * the browser and then be dropped silently: the candidate saw the "here are
 * three tracks" sentence with nothing under it until a *later* turn happened
 * to restate them as `choices`. Both fields render through the same lettered
 * list, `choices` taking priority since a turn never sets both.
 *
 * The free-text box is a textarea that grows with what is typed, not a
 * single-line input: the questions are open-ended ones about a career, and a
 * field that shows one line of a four-line answer invites a one-line answer.
 * Enter sends, Shift+Enter starts a new line, and the Send button stays
 * visible so neither has to be guessed.
 */

import { useEffect, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'
import { streamInterview } from '../api/onboarding'
import { ApiError, isAbortError } from '../api/client'
import { useI18n } from '../i18n'
import { Thinking } from './Thinking'
import type { OnboardingIntent } from '../types/domain'
import './InterviewChat.css'

interface ChatTurn {
  id: number
  role: 'assistant' | 'user'
  text: string
  choices?: string[]
  choicesMulti?: boolean
  suggestedDirections?: string[]
}

/**
 * Option labels, in order. The backend caps `choices` at 3, so the free-text
 * option lands on D at most — index `choices.length` is always in range.
 */
const OPTION_LETTERS = ['A', 'B', 'C', 'D']

/**
 * The interview wire format is plain text. A model can still copy HTML line
 * breaks into a field. Convert only that harmless formatting token and leave
 * every other tag as text, so React remains the escaping boundary.
 */
function plainInterviewText(text: string): string {
  return text.replace(/<br\s*\/?\s*>/gi, '\n')
}

interface InterviewChatProps {
  /**
   * The PIVOT-075 "why are you here" choice, made just before this screen.
   * Only sent on the mount-time turn, which is what constructs the
   * `InterviewSession` server-side (PIVOT-076) — passing it again on later
   * turns would arrive after the session already exists.
   *
   * **Omitted when the page resumed straight into the interview** (a reload
   * mid-conversation, `pages/Onboarding.tsx`). The session already exists then,
   * and the backend's replay path never reads `intent` — but it *would* read it
   * if the draft had been dropped, and sending a freshly-guessed intent there
   * would silently reshape a conversation the candidate already started. Not
   * sending one leaves the server on the intent the session was opened with.
   */
  intent?: OnboardingIntent
  onDone: (answers: Record<string, string>) => void
}

export function InterviewChat({ intent, onDone }: InterviewChatProps) {
  const { t } = useI18n()
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [input, setInput] = useState('')
  /**
   * Multi-select picks, tagged with the assistant turn they were made on.
   *
   * The tag is the guard. A pick left over from the previous question would be
   * sent as the answer to this one — the candidate would see a clean list, tap
   * one option, and confirm two — so `pickedOn` has to match the turn on
   * screen for the picks to count. Every new turn gets a fresh id, which makes
   * the reset structural instead of something a future edit has to remember to
   * do on each of the paths that start a turn.
   */
  const [picks, setPicks] = useState<{ pickedOn: number; labels: string[] }>({
    pickedOn: 0,
    labels: [],
  })
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** A stale-consent 403 needs its own action state (PIVOT-299 review): the
   * candidate has to renew consent on their profile, and the common error
   * message never says that. Separate from `error` so the two never render
   * on top of each other. */
  const [consentStale, setConsentStale] = useState(false)
  const answersRef = useRef<Record<string, string>>({})
  const nextIdRef = useRef(0)
  const turnCountRef = useRef(0)
  const openedRef = useRef(false)
  /** The turn currently streaming, so unmount can close its connection. */
  const stream = useRef<AbortController | null>(null)
  /** Set while an abort is queued, cleared if the effect comes straight back. */
  const closing = useRef(false)
  const transcript = useRef<HTMLUListElement>(null)

  const nextId = () => {
    nextIdRef.current += 1
    return nextIdRef.current
  }

  const runTurn = async (userMessage: string | null = null, turnIntent?: OnboardingIntent) => {
    const controller = new AbortController()
    stream.current = controller
    setStreaming(true)
    setError(null)
    setConsentStale(false)
    if (userMessage) {
      setTurns((prev) => [...prev, { id: nextId(), role: 'user', text: userMessage }])
    }
    const assistantId = nextId()
    setTurns((prev) => [...prev, { id: assistantId, role: 'assistant', text: '' }])

    try {
      // The route emits exactly one `event: turn` block per request — a full
      // JSON-serialized InterviewTurn, not raw token chunks (see
      // `interview_turn`'s docstring) — so each iteration replaces the
      // assistant's text rather than appending to it, and completion comes
      // from the turn's own `done` field. There is no separate `done` SSE
      // event; the wire format never sends one.
      for await (const chunk of streamInterview(userMessage, turnIntent, controller.signal)) {
        const turn = JSON.parse(chunk.data) as {
          message: string
          done: boolean
          choices?: string[]
          choices_multi?: boolean
          suggested_directions?: string[]
        }
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantId
              ? {
                  ...t,
                  text: plainInterviewText(turn.message),
                  choices: turn.choices?.map(plainInterviewText),
                  choicesMulti: turn.choices_multi,
                  suggestedDirections: turn.suggested_directions?.map(plainInterviewText),
                }
              : t,
          ),
        )
        if (turn.done) {
          onDone(answersRef.current)
        }
      }
    } catch (reason) {
      // Leaving the step mid-turn aborts the stream; that is not an error.
      if (isAbortError(reason)) return
      // A stale-consent 403 (`PRIVACY_POLICY_VERSION` bumped since the
      // candidate last consented) needs its own action state, not the common
      // error: the fix is to renew consent on their profile, not to retry the
      // same reply (PIVOT-299 review).
      if (reason instanceof ApiError && reason.status === 403) {
        setConsentStale(true)
      } else {
        setError(t('common.error'))
      }
    } finally {
      if (stream.current === controller) {
        stream.current = null
        setStreaming(false)
      }
    }
  }

  useEffect(() => {
    // A re-entry cancels a queued abort — see the cleanup below.
    closing.current = false
    // Guarded, not just mount-only: StrictMode invokes effects twice in dev, and
    // two opens in flight would race on the server's stored interview state.
    if (openedRef.current) return
    openedRef.current = true
    void runTurn(null, intent)
    // Closes whichever turn is streaming when the step goes away — but only
    // *queues* it. StrictMode's dev remount runs this cleanup and then sets the
    // effect straight back up in the same tick, so aborting here outright killed
    // the opening request: the guard above stopped the second run from
    // re-issuing it, and the step sat on an empty bubble with no question and no
    // error to explain it. Microtasks run after that synchronous teardown-setup
    // pair, so a remount clears the flag in time and a real unmount doesn't.
    return () => {
      closing.current = true
      queueMicrotask(() => {
        if (closing.current) stream.current?.abort()
      })
    }
    // Opens the interview with the server's first question; intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Pin the transcript to the newest turn. The box scrolls internally rather
    // than growing the page, so without this a reply and the answer to it land
    // below the fold and the conversation looks stuck on an old question. Jumps
    // rather than smooth-scrolls, so there is no motion to reduce.
    const box = transcript.current
    if (box) box.scrollTop = box.scrollHeight
  }, [turns])

  const submitAnswer = (text: string) => {
    if (!text || streaming) return
    turnCountRef.current += 1
    answersRef.current[`turn_${turnCountRef.current}`] = text
    setInput('')
    void runTurn(text)
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    submitAnswer(input.trim())
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter is a newline, plain Enter sends. `isComposing` is the trap:
    // with an IME (or a phone's suggestion bar) Enter commits the word being
    // composed, and treating that as "send" fires a half-typed answer.
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
    e.preventDefault()
    submitAnswer(input.trim())
  }

  // The options belong to the turn on screen, so they are read off the last
  // turn rather than kept in state. Mid-stream they are gone: the next
  // question's choices are not known yet, and the previous one's no longer apply.
  // A "directions" turn carries its options as `suggestedDirections` rather
  // than `choices` — a turn never sets both, so `choices` wins when present.
  const lastTurn = turns[turns.length - 1]
  const choices =
    !streaming && lastTurn?.role === 'assistant'
      ? lastTurn.choices?.length
        ? lastTurn.choices
        : lastTurn.suggestedDirections?.length
          ? lastTurn.suggestedDirections
          : null
      : null

  // Multi-select is a property of the turn, so it applies to whichever of the
  // two option fields rendered above.
  const multiSelect = choices !== null && lastTurn?.choicesMulti === true
  const selected = lastTurn && picks.pickedOn === lastTurn.id ? picks.labels : []
  const togglePick = (choice: string) => {
    if (!lastTurn) return
    const labels = selected.includes(choice)
      ? selected.filter((label) => label !== choice)
      : [...selected, choice]
    setPicks({ pickedOn: lastTurn.id, labels })
  }

  return (
    <div className="stack">
      <h2>{t('onboarding.interviewTitle')}</h2>
      <p className="interview-chat-intro">{t('onboarding.interviewIntro')}</p>
      <ul className="interview-chat-turns" ref={transcript}>
        {turns.map((turn, index) => (
          <li key={turn.id} className={`chat-turn chat-turn-${turn.role}`}>
            {turn.text ||
              (streaming && index === turns.length - 1 ? (
                // PIVOT-143: the longest, most-repeated LLM wait in the product
                // used to render the bare rail — no caption, so between sweeps
                // it read as broken rather than busy. See Thinking.tsx's own
                // docstring for why omitting these is no longer the default.
                <Thinking
                  label="onboarding.interviewThinking"
                  patience="onboarding.interviewThinkingPatience"
                />
              ) : null)}
          </li>
        ))}
      </ul>
      {multiSelect && (
        <p className="interview-chat-multi-hint">{t('onboarding.interviewMultiHint')}</p>
      )}
      {choices && (
        <ul
          className="interview-chat-options"
          aria-label={multiSelect ? t('a11y.interviewMultiOptions') : undefined}
        >
          {choices.map((choice, index) => (
            <li key={choice}>
              <button
                type="button"
                className="interview-chat-option"
                // A toggle in multi mode, so it announces as one. The pressed
                // state is also what the stylesheet selects on, so what is
                // read out and what is painted cannot disagree.
                aria-pressed={multiSelect ? selected.includes(choice) : undefined}
                onClick={() => (multiSelect ? togglePick(choice) : submitAnswer(choice))}
              >
                <span className="interview-chat-option-letter">{OPTION_LETTERS[index]}</span>
                <span>{choice}</span>
                {selected.includes(choice) && (
                  <span className="interview-chat-option-check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {multiSelect && (
        <button
          type="button"
          className="btn btn-primary interview-chat-confirm"
          disabled={selected.length === 0}
          onClick={() => submitAnswer(selected.join('; '))}
        >
          {t('onboarding.interviewConfirm')}
        </button>
      )}
      {consentStale && (
        <p className="form-error" role="alert">
          {t('onboarding.interviewConsentStale')}
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
      <form className="interview-chat-write" onSubmit={handleSubmit}>
        {choices && (
          <p className="interview-chat-write-head">
            <span className="interview-chat-option-letter">{OPTION_LETTERS[choices.length]}</span>
            {t('onboarding.interviewWriteOption')}
          </p>
        )}
        <div className="interview-chat-write-row">
          {/* `.grow-field` (styles/primitives.css, PIVOT-145): mirrors the
              text into a hidden `::after` in the same grid cell, so the row's
              height is the text's own height. CSS-only, and it cannot fall
              out of step with the field the way a scrollHeight measurement
              can. */}
          <div className="grow-field" data-replicated-value={input}>
            <textarea
              value={input}
              rows={1}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('onboarding.interviewPlaceholder')}
              aria-label={t('a11y.interviewAnswer')}
              disabled={streaming}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={streaming || !input.trim()}>
            {t('onboarding.interviewSend')}
          </button>
        </div>
        <p className="interview-chat-write-hint">{t('onboarding.interviewSendHint')}</p>
      </form>
    </div>
  )
}
